// @ts-nocheck
import React, { useState } from 'react';
import {
  BookOpen, Upload, Sparkles, GraduationCap, Wand2, Globe, Shield, Brain, Settings2,
} from 'lucide-react';
import KnowledgeBase from '../../pages/KnowledgeBase';
import DocumentUpload from '../../pages/DocumentUpload';
import Prompts from '../../pages/Prompts';
import FineTuningDashboard from '../../pages/FineTuningDashboard';
import ThemeEditor from '../DynamicTheming/ThemeEditorNew';
import AllowedDomainsSettings from '../settings/AllowedDomainsSettings';
import CitationSettings from '../settings/CitationSettings';
import RAGPreferencesSettings from '../settings/RAGPreferencesSettings';
import ConnectionsSettings from '../settings/ConnectionsSettings';

interface ChatbotCustomizeTabProps {
  clientId: string;
}

type SectionType = 'theme' | 'sources' | 'import' | 'prompts' | 'training' | 'settings';

const sections: { id: SectionType; label: string; icon: React.ElementType; description: string }[] = [
  { id: 'theme', label: 'Appearance', icon: Wand2, description: 'Colors, fonts, and branding' },
  { id: 'sources', label: 'Sources', icon: BookOpen, description: 'Website content and data' },
  { id: 'import', label: 'Import', icon: Upload, description: 'Upload documents and files' },
  { id: 'prompts', label: 'Prompts', icon: Sparkles, description: 'System prompts and behavior' },
  { id: 'training', label: 'Training', icon: GraduationCap, description: 'Fine-tuning and QA' },
  { id: 'settings', label: 'Settings', icon: Settings2, description: 'Domains, citations, AI behavior' },
];

const ChatbotCustomizeTab: React.FC<ChatbotCustomizeTabProps> = ({ clientId }) => {
  const [activeSection, setActiveSection] = useState<SectionType>('theme');

  return (
    <div className="flex gap-6 min-h-[600px]">
      {/* Left sidebar nav */}
      <div className="w-56 shrink-0">
        <nav className="space-y-1 sticky top-4">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon size={18} className={`mt-0.5 shrink-0 ${isActive ? 'text-brand-500' : 'text-gray-400'}`} />
                <div>
                  <div className="text-sm font-medium">{section.label}</div>
                  <div className={`text-xs ${isActive ? 'text-brand-500' : 'text-gray-400'}`}>
                    {section.description}
                  </div>
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Right content */}
      <div className="flex-1 min-w-0">
        {activeSection === 'theme' && <ThemeEditor clientId={clientId} />}
        {activeSection === 'sources' && <KnowledgeBase clientId={clientId} />}
        {activeSection === 'import' && <DocumentUpload clientId={clientId} />}
        {activeSection === 'prompts' && <Prompts />}
        {activeSection === 'training' && <FineTuningDashboard />}
        {activeSection === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Globe size={18} className="text-gray-400" />
                Allowed Domains
              </h3>
              <AllowedDomainsSettings clientId={clientId} />
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Brain size={18} className="text-gray-400" />
                Citations
              </h3>
              <CitationSettings clientId={clientId} onStatusChange={() => {}} />
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield size={18} className="text-gray-400" />
                Source Preferences
              </h3>
              <RAGPreferencesSettings clientId={clientId} />
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings2 size={18} className="text-gray-400" />
                Connections
              </h3>
              <ConnectionsSettings />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatbotCustomizeTab;
